import { createFileRoute, Link } from '@tanstack/react-router'
import { CalendarRangeIcon, ListIcon, TagIcon, UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDispatches } from '@/api/dispatches'
import { useReturns } from '@/api/returns'
import { useSalesChannel } from '@/api/sales-channels'
import type { SalesChannelDetail } from '@/api/sales-channels'
import { useSalesReports } from '@/api/sales-reports'
import { useWorkspace } from '@/api/workspace'
import { VoidedBadge } from '@/components/corrections/voided'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { todayIn, unreportedDaysAfter } from '@/components/sales-reports/format'
import { StatusBadge } from '@/components/sales-reports/status-badge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

// Sales Channel detail (UI-SCREENS S-03, 05a #169, ADR 0012): the contact block, Stock at Channel
// per Variant and the Dispatches / Returns / Sales Reports tabs, which fill as 05c #171, 05d #172
// and 06b #185 land. Payments and outstanding come with the Payments slice; the Vendor Price
// overrides block landed with 06a #184.
export const Route = createFileRoute('/w/$slug/channels/$channelId/')({
  component: SalesChannelDetailScreen,
  staticData: { breadcrumb: 'salesChannels.detail.title' },
})

function SalesChannelDetailScreen() {
  const { t } = useTranslation()
  const { slug, channelId } = Route.useParams()
  const channel = useSalesChannel(slug, Number(channelId))

  if (channel.isPending) return <LoadingSkeleton variant="page" />
  if (channel.isError || !channel.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const data = channel.data
  const rows: [string, string][] = [
    [t('salesChannels.detail.kind'), t(`salesChannels.kinds.${data.kind === 'direct' ? 'direct' : 'consignment'}`)],
    [t('salesChannels.detail.contactName'), data.contact_name ?? '—'],
    [t('salesChannels.detail.contactPhone'), data.contact_phone ?? '—'],
    [t('salesChannels.detail.address'), data.address ?? '—'],
    [t('salesChannels.detail.settlementCycle'), data.settlement_cycle ?? '—'],
    [
      t('salesChannels.detail.settlementCycleDays'),
      data.settlement_cycle_days === null ? '—' : String(data.settlement_cycle_days),
    ],
    [t('salesChannels.detail.notes'), data.notes ?? '—'],
  ]

  return (
    <section className="flex max-w-3xl flex-col gap-4">
      <Link to="/w/$slug/channels" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('salesChannels.back')}
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-xl font-semibold">{data.name}</h1>
        {data.archived_at ? <Badge variant="outline">{t('salesChannels.archived')}</Badge> : null}
        {/* What this channel still owes over its live Sales Reports (S-09's figure for one
            channel). A revenue figure, so the server omits it for Staff (G2). */}
        {data.outstanding_amount === undefined ? null : (
          <span className="ml-auto text-sm">
            <span className="text-muted-foreground">{t('payments.outstanding')}: </span>
            <span className="font-medium">Rp {formatMoney(data.outstanding_amount)}</span>
          </span>
        )}
      </div>
      {data.can.update ? (
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="h-11">
            <Link to="/w/$slug/channels/$channelId/edit" params={{ slug, channelId }}>
              {t('salesChannels.edit')}
            </Link>
          </Button>
        </div>
      ) : null}

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[max-content_1fr]">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-sm text-muted-foreground">{label}</dt>
            <dd className="font-medium whitespace-pre-line">{value}</dd>
          </div>
        ))}
      </dl>

      <StockAtChannel rows={data.stock_at_channel} units={data.units_at_channel} />

      <VendorPriceOverrides rows={data.vendor_price_overrides} />

      <Tabs defaultValue="dispatches">
        <TabsList>
          <TabsTrigger value="dispatches">{t('salesChannels.detail.dispatches')}</TabsTrigger>
          <TabsTrigger value="returns">{t('salesChannels.detail.returns')}</TabsTrigger>
          <TabsTrigger value="sales-reports">{t('salesChannels.detail.salesReports')}</TabsTrigger>
          {data.payments === undefined ? null : <TabsTrigger value="payments">{t('payments.title')}</TabsTrigger>}
        </TabsList>
        <TabsContent value="dispatches" className="pt-3">
          <ChannelDispatches slug={slug} channelId={Number(channelId)} />
        </TabsContent>
        <TabsContent value="returns" className="pt-3">
          <ChannelReturns slug={slug} channelId={Number(channelId)} />
        </TabsContent>
        <TabsContent value="sales-reports" className="pt-3">
          <ChannelSalesReports slug={slug} channelId={Number(channelId)} />
        </TabsContent>
        {data.payments === undefined ? null : (
          <TabsContent value="payments" className="pt-3">
            <ChannelPayments slug={slug} payments={data.payments} />
          </TabsContent>
        )}
      </Tabs>
    </section>
  )
}

// This channel's Dispatches (UI-SCREENS S-03, #242): newest first, each row opening I-06.
function ChannelDispatches({ slug, channelId }: { slug: string; channelId: number }) {
  const { t } = useTranslation()
  const dispatches = useDispatches(slug, { salesChannelId: channelId })

  if (dispatches.isPending) return <LoadingSkeleton rows={3} />
  if (dispatches.isError || !dispatches.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }
  if (dispatches.data.length === 0) {
    return <RecordListEmpty>{t('salesChannels.detail.noDispatches')}</RecordListEmpty>
  }

  return (
    <RecordList>
      {dispatches.data.map((dispatch) => (
        <RecordListItem
          key={dispatch.id}
          voided={Boolean(dispatch.voided_at)}
          title={
            <RecordLink to="/w/$slug/dispatches/$dispatchId" params={{ slug, dispatchId: String(dispatch.id) }}>
              {dispatch.dispatch_no}
            </RecordLink>
          }
          badges={<VoidedBadge record={dispatch} />}
          meta={[
            { icon: ListIcon, label: t('dispatches.detail.lines'), value: t('dispatches.list.lines', { count: dispatch.lines.length }) },
            { icon: UserIcon, label: t('dispatches.columns.createdBy'), value: dispatch.created_by.name ?? dispatch.created_by.email },
          ]}
          description={dispatch.note || undefined}
          figure={t('dispatches.units', { count: dispatch.total_units })}
          caption={formatBusinessDate(dispatch.occurred_on)}
        />
      ))}
    </RecordList>
  )
}

// This channel's Returns (UI-SCREENS S-03, 05d #172): newest first, each row opening I-09.
function ChannelReturns({ slug, channelId }: { slug: string; channelId: number }) {
  const { t } = useTranslation()
  const returns = useReturns(slug, { salesChannelId: channelId })

  if (returns.isPending) return <LoadingSkeleton rows={3} />
  if (returns.isError || !returns.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }
  if (returns.data.length === 0) {
    return <RecordListEmpty>{t('salesChannels.detail.noReturns')}</RecordListEmpty>
  }

  return (
    <RecordList>
      {returns.data.map((document) => (
        <RecordListItem
          key={document.id}
          voided={Boolean(document.voided_at)}
          title={
            <RecordLink to="/w/$slug/returns/$returnId" params={{ slug, returnId: String(document.id) }}>
              {document.return_no}
            </RecordLink>
          }
          badges={<VoidedBadge record={document} />}
          meta={[
            { icon: ListIcon, label: t('returns.detail.lines'), value: t('returns.list.lines', { count: document.lines.length }) },
            { icon: UserIcon, label: t('returns.columns.createdBy'), value: document.created_by.name ?? document.created_by.email },
          ]}
          description={document.note || undefined}
          figure={t('returns.units', { count: document.total_units })}
          caption={formatBusinessDate(document.occurred_on)}
        />
      ))}
    </RecordList>
  )
}

// This channel's Sales Reports (UI-SCREENS S-03, 06b #185): newest first, each row opening S-07,
// with the **unreported days** each period leaves before the next one — the gaps are allowed, the
// screen only says how many (worked-examples S-1).
function ChannelSalesReports({ slug, channelId }: { slug: string; channelId: number }) {
  const { t } = useTranslation()
  const workspace = useWorkspace(slug)
  const reports = useSalesReports(slug, { salesChannelId: channelId })

  if (reports.isPending || workspace.isPending) return <LoadingSkeleton rows={3} />
  if (reports.isError || !reports.data || workspace.isError || !workspace.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }
  if (reports.data.length === 0) {
    return <RecordListEmpty>{t('salesChannels.detail.noSalesReports')}</RecordListEmpty>
  }

  const live = reports.data.filter((report) => !report.voided_at)
  const gaps = unreportedDaysAfter(live, todayIn(workspace.data.timezone))

  return (
    <RecordList>
      {reports.data.map((report) => {
        const gap = gaps[live.indexOf(report)] ?? 0
        return (
          <RecordListItem
            key={report.id}
            voided={Boolean(report.voided_at)}
            title={
              <RecordLink to="/w/$slug/sales-reports/$reportId" params={{ slug, reportId: String(report.id) }}>
                {report.sales_report_no}
              </RecordLink>
            }
            badges={
              <>
                <StatusBadge report={report} />
                <VoidedBadge record={report} />
              </>
            }
            meta={[
              {
                icon: CalendarRangeIcon,
                label: t('salesReports.columns.period'),
                value: t('salesReports.period', {
                  from: formatBusinessDate(report.period_start),
                  to: formatBusinessDate(report.period_end),
                }),
              },
              { icon: UserIcon, label: t('salesReports.columns.createdBy'), value: report.created_by.name ?? report.created_by.email },
            ]}
            description={gap > 0 ? t('salesReports.unreportedDays', { count: gap }) : undefined}
            figure={t('salesReports.units', { count: report.total_units })}
            caption={formatBusinessDate(report.occurred_on)}
          />
        )
      })}
    </RecordList>
  )
}

// This channel's Payments across its Sales Reports (UI-SCREENS S-03, 06c #186): newest first,
// voided ones included — they stay visible and simply left the paid total. Each row opens the
// report it paid. Owner only: the server omits the list for Staff (G2), so the tab isn't shown.
function ChannelPayments({
  slug,
  payments,
}: {
  slug: string
  payments: NonNullable<SalesChannelDetail['payments']>
}) {
  const { t } = useTranslation()

  if (payments.length === 0) return <RecordListEmpty>{t('payments.empty')}</RecordListEmpty>

  return (
    <RecordList>
      {payments.map((payment) => (
        <RecordListItem
          key={payment.id}
          voided={Boolean(payment.voided_at)}
          title={
            <RecordLink to="/w/$slug/sales-reports/$reportId" params={{ slug, reportId: String(payment.sales_report_id) }}>
              {payment.payment_no}
            </RecordLink>
          }
          badges={<VoidedBadge record={payment} />}
          meta={[
            { icon: UserIcon, label: t('salesReports.columns.createdBy'), value: payment.created_by.name ?? payment.created_by.email },
          ]}
          description={payment.note || undefined}
          figure={`Rp ${formatMoney(payment.amount)}`}
          caption={formatBusinessDate(payment.occurred_on)}
        />
      ))}
    </RecordList>
  )
}

// The channel's Vendor Price overrides (UI-SCREENS S-03, 06a #184): the Variants with an
// override here and the date it took effect. The server sends an empty list to Staff — Vendor
// Prices are revenue figures they never see (G3) — so the block reads as "none" for them.
function VendorPriceOverrides({ rows }: { rows: SalesChannelDetail['vendor_price_overrides'] }) {
  const { t } = useTranslation()

  return (
    <section className="flex flex-col gap-2">
      <h2 className="font-heading font-semibold">{t('vendorPrices.atChannel')}</h2>
      {rows.length === 0 ? (
        <RecordListEmpty>{t('vendorPrices.noneAtChannel')}</RecordListEmpty>
      ) : (
        <RecordList>
          {rows.map((row) => (
            <RecordListItem
              key={row.variant_id}
              title={`${row.product_name} · ${row.name}`}
              badges={row.archived ? <Badge variant="outline">{t('vendorPrices.archived')}</Badge> : null}
              meta={[{ icon: TagIcon, label: t('variants.form.sku'), value: row.sku }]}
              figure={`Rp ${formatMoney(row.price)}`}
              caption={`${t('vendorPrices.effectiveFrom')}: ${formatBusinessDate(row.effective_from)}`}
            />
          ))}
        </RecordList>
      )}
    </section>
  )
}

function StockAtChannel({ rows, units }: { rows: SalesChannelDetail['stock_at_channel']; units: number }) {
  const { t } = useTranslation()

  return (
    <section className="flex flex-col gap-2">
      <h2 className="font-heading font-semibold">{t('salesChannels.detail.stock')}</h2>
      <p className="text-sm text-muted-foreground">{t('salesChannels.unitsAtChannel', { count: units })}</p>
      {rows.length === 0 ? (
        <RecordListEmpty>{t('salesChannels.detail.noStock')}</RecordListEmpty>
      ) : (
        <RecordList>
          {rows.map((row) => (
            <RecordListItem
              key={row.variant_id}
              title={`${row.product_name} · ${row.name}`}
              meta={[{ icon: TagIcon, label: t('variants.form.sku'), value: row.sku }]}
              figure={t('salesChannels.list.units', { count: row.qty })}
            />
          ))}
        </RecordList>
      )}
    </section>
  )
}
